import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class ClinicalStudy extends UcoastEl {
	static htmlSelector = 'clinical-study'
	private slides: HTMLElement[]
	private currentIndex = 0
	private autoplayInterval?: number
	private autoplaySpeed: number
	private progressBar?: HTMLElement
	private progressCurrent?: HTMLElement
	private statDisplay?: HTMLElement
	private subtitleDisplay?: HTMLElement
	private reviewDisplay?: HTMLElement
	private prevButton?: HTMLButtonElement
	private nextButton?: HTMLButtonElement
	private hasAnimated = false
	private observer?: IntersectionObserver
	private beforeContainer?: HTMLElement
	private afterContainer?: HTMLElement
	private beforeLabel?: HTMLElement
	private afterLabel?: HTMLElement
	private imagePair?: HTMLElement

	constructor() {
		super()
		this.slides = Array.from(this.querySelectorAll('[data-study-slide]'))
		this.autoplaySpeed = parseInt(this.dataset.speed ?? '10') * 1000
		this.progressBar = q.os('.clinical-study__progress-fill', this)
		this.progressCurrent = q.os('.clinical-study__progress-current', this)
		this.statDisplay = q.os('.clinical-study__stat-number', this)
		this.subtitleDisplay = q.os('.clinical-study__stat-text', this)
		this.reviewDisplay = q.os('.clinical-study__review', this)
		this.prevButton = q.os('button[name="previous"]', this)
		this.nextButton = q.os('button[name="next"]', this)
		this.imagePair = q.os('.clinical-study__image-pair', this)
		this.beforeContainer = q.os('[data-clinical-before]', this)
		this.afterContainer = q.os('[data-clinical-after]', this)
		this.beforeLabel = q.os('.clinical-study__image--before .clinical-study__image-label', this)
		this.afterLabel = q.os('.clinical-study__image--after .clinical-study__image-label', this)

		this.prevButton?.addEventListener('click', this.prev.bind(this))
		this.nextButton?.addEventListener('click', this.next.bind(this))

		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !this.hasAnimated) {
						this.hasAnimated = true
						this.animateCountUp()
						this.startAutoplay()
						this.observer?.disconnect()
					}
				})
			},
			{ threshold: 0.3 }
		)
		this.observer.observe(this)

		this.showSlide(0)
	}

	private animateCountUp() {
		if (!this.statDisplay) return
		const target = parseInt(this.statDisplay.dataset.target ?? '0')
		const duration = 2000
		const startTime = performance.now()

		const step = (currentTime: number) => {
			const elapsed = currentTime - startTime
			const progress = Math.min(elapsed / duration, 1)
			const eased = 1 - Math.pow(1 - progress, 3) // easeOutCubic
			const current = Math.round(eased * target)

			if (this.statDisplay) {
				this.statDisplay.textContent = `${current}%`
			}

			if (progress < 1) {
				requestAnimationFrame(step)
			}
		}

		requestAnimationFrame(step)
	}

	private showSlide(index: number) {
		this.currentIndex = index
		this.slides.forEach((slide, i) => {
			const isActive = i === index
			slide.classList.toggle('clinical-study__slide--active', isActive)
			slide.setAttribute('aria-hidden', isActive ? 'false' : 'true')
		})

		const activeSlide = this.slides[index]
		if (!activeSlide) return

		// Update stat display
		if (this.statDisplay) {
			const target = activeSlide.dataset.statNumber ?? '0'
			this.statDisplay.dataset.target = target
			if (this.hasAnimated) {
				this.statDisplay.textContent = `${target}%`
			}
		}

		// Update subtitle
		if (this.subtitleDisplay) {
			this.subtitleDisplay.textContent = activeSlide.dataset.statText ?? ''
		}

		// Update review
		if (this.reviewDisplay) {
			const reviewContent = activeSlide.querySelector('.clinical-study__slide-review')
			if (reviewContent) {
				this.reviewDisplay.innerHTML = reviewContent.innerHTML
			}
		}

		// Update before/after images
		this.updateBeforeAfter(activeSlide)

		// Update progress
		this.updateProgress()
		this.resetAutoplayProgress()
	}

	private updateProgress() {
		if (!this.progressBar) return
		const progress = (this.currentIndex + 1) / this.slides.length
		this.progressBar.style.transform = `scaleX(${progress})`
		if (!this.progressCurrent) return
		this.progressCurrent.textContent = (this.currentIndex + 1).toString()
	}

	private next() {
		const nextIndex = (this.currentIndex + 1) % this.slides.length
		this.showSlide(nextIndex)
		this.restartAutoplay()
	}

	private prev() {
		const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length
		this.showSlide(prevIndex)
		this.restartAutoplay()
	}

	private startAutoplay() {
		if (this.autoplaySpeed <= 0) return
		this.autoplayInterval = window.setInterval(() => this.next(), this.autoplaySpeed)
	}

	private restartAutoplay() {
		if (this.autoplayInterval) clearInterval(this.autoplayInterval)
		this.startAutoplay()
	}

	private resetAutoplayProgress() {
		// Progress bar is handled by updateProgress
	}

	private updateBeforeAfter(activeSlide: HTMLElement) {
		if (!this.beforeContainer || !this.afterContainer) return

		const beforeSrc = activeSlide.dataset.beforeSrc
		const beforeSrcset = activeSlide.dataset.beforeSrcset
		const beforeWidth = activeSlide.dataset.beforeWidth
		const beforeHeight = activeSlide.dataset.beforeHeight
		const beforeAlt = activeSlide.dataset.beforeAlt
		const beforeSizes = activeSlide.dataset.beforeSizes
		const afterSrc = activeSlide.dataset.afterSrc
		const afterSrcset = activeSlide.dataset.afterSrcset
		const afterWidth = activeSlide.dataset.afterWidth
		const afterHeight = activeSlide.dataset.afterHeight
		const afterAlt = activeSlide.dataset.afterAlt
		const afterSizes = activeSlide.dataset.afterSizes
		const hasAfterImage = activeSlide.dataset.hasAfterImage === 'true' && Boolean(afterSrc)

		this.renderMedia(this.beforeContainer, {
			src: beforeSrc,
			srcset: beforeSrcset,
			width: beforeWidth,
			height: beforeHeight,
			alt: beforeAlt ?? 'Clinical study image',
			sizes: beforeSizes ?? '(max-width: 989px) 100vw, 50vw',
		})

		if (hasAfterImage) {
			this.renderMedia(this.afterContainer, {
				src: afterSrc,
				srcset: afterSrcset,
				width: afterWidth,
				height: afterHeight,
				alt: afterAlt ?? 'Clinical study after image',
				sizes: afterSizes ?? '(max-width: 989px) 100vw, 25vw',
			})
		} else {
			this.afterContainer.innerHTML = ''
		}

		this.imagePair?.classList.toggle('clinical-study__image-pair--single', !hasAfterImage)
		this.toggleElement(this.afterContainer.closest('.clinical-study__image') as HTMLElement | undefined, hasAfterImage)
		this.toggleElement(this.beforeLabel, hasAfterImage)
		this.toggleElement(this.afterLabel, hasAfterImage)
	}

	private renderMedia(
		container: HTMLElement,
		media: {
			src?: string
			srcset?: string
			width?: string
			height?: string
			alt: string
			sizes: string
		}
	) {
		if (!media.src) {
			container.innerHTML =
				'<div class="clinical-study__placeholder"><svg class="placeholder-svg" viewBox="0 0 525.8 525.8" role="img" aria-label="Placeholder image"><path d="M266.6 491.4L503 255H28.3zm0 0" fill="currentColor" fill-opacity=".1"/><path d="M396 294.4c-2 0-4-.8-5.5-2.3l-68.8-68.7-57.5 57.5c-1.5 1.5-3.5 2.3-5.5 2.3s-4-.8-5.5-2.3l-30.5-30.6-47.8 47.8c-3 3-8 3-11 0-3-3-3-8 0-11l53.3-53.3c3-3 8-3 11 0l30.5 30.6 57.5-57.5c3-3 8-3 11 0l74.3 74.3c3 3 3 8 0 11-1.5 1.6-3.5 2.4-5.5 2.4" fill="currentColor" fill-opacity=".1"/><path d="M418.6 192.7c-16.4 0-29.8-13.4-29.8-29.8s13.4-29.8 29.8-29.8 29.8 13.4 29.8 29.8-13.4 29.8-29.8 29.8m0-44c-7.8 0-14.2 6.4-14.2 14.2s6.4 14.2 14.2 14.2 14.2-6.4 14.2-14.2-6.4-14.2-14.2-14.2" fill="currentColor" fill-opacity=".1"/><path d="M0 0v525.8h525.8V0zm503 503H22.8V22.8H503zm0 0" fill="currentColor" fill-opacity=".15"/></svg></div>'
			return
		}

		const img = document.createElement('img')
		img.src = media.src
		if (media.srcset) img.srcset = media.srcset
		img.sizes = media.sizes
		img.alt = media.alt
		img.loading = 'lazy'
		if (media.width) img.width = parseInt(media.width, 10)
		if (media.height) img.height = parseInt(media.height, 10)
		container.replaceChildren(img)
	}

	private toggleElement(element: HTMLElement | HTMLInputElement | undefined, shouldShow: boolean) {
		if (!element) return
		element.hidden = !shouldShow
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (this.autoplayInterval) clearInterval(this.autoplayInterval)
		this.observer?.disconnect()
	}
}
